import { ArrowUpTrayIcon, DocumentIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useMemo, useState } from "react";
import { FileList, fileKind, UploadDialog } from "@/components/files/Files";
import { Breadcrumbs } from "@/components/page";
import { Button, EmptyState, PageSpinner } from "@/components/ui";
import { useProject } from "@/lib/project";
import { useFiles } from "@/lib/queries";
import { cn, formatBytes } from "@/lib/utils";

const kinds = [
  { id: "all", label: "All" },
  { id: "pdf", label: "PDF" },
  { id: "doc", label: "Documents" },
  { id: "sheet", label: "Spreadsheets" },
  { id: "image", label: "Images" },
  { id: "other", label: "Other" },
];

export function FilesPage() {
  const { pid, bundle, canEdit } = useProject();
  const files = useFiles(pid);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [linked, setLinked] = useState<"all" | "linked" | "unlinked">("all");
  const [uploading, setUploading] = useState(false);
  const [dropped, setDropped] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (files.data ?? []).filter(
      (f) =>
        (kind === "all" || fileKind(f.mimeType, f.name) === kind) &&
        (linked === "all" || (linked === "linked" ? f.links.length > 0 : f.links.length === 0)) &&
        (!term ||
          f.name.toLowerCase().includes(term) ||
          f.description.toLowerCase().includes(term) ||
          f.links.some((l) => l.title.toLowerCase().includes(term))),
    );
  }, [files.data, q, kind, linked]);
  const totalSize = (files.data ?? []).reduce((n, f) => n + f.sizeBytes, 0);

  return (
    <div
      className="min-h-full"
      onDragOver={(e) => {
        if (!canEdit || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setDragging(false);
        setDropped(Array.from(e.dataTransfer.files));
        setUploading(true);
      }}
    >
      <Breadcrumbs items={[{ label: bundle.project.name, to: `/p/${pid}` }, { label: "Files" }]} />
      <div className={cn("mx-auto max-w-5xl px-6 pt-8 pb-24 md:px-12", dragging && "opacity-60")}>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[32px] leading-tight font-bold">Files</h1>
            <p className="mt-1 text-sm text-muted">
              Documents, PDFs and images for this project{files.data?.length ? ` · ${files.data.length} files, ${formatBytes(totalSize)}` : ""}. Drop files anywhere on this page to upload.
            </p>
          </div>
          {canEdit && (
            <Button variant="primary" icon={ArrowUpTrayIcon} onClick={() => setUploading(true)}>
              Upload
            </Button>
          )}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-48 flex-1 sm:max-w-72">
            <MagnifyingGlassIcon className="pointer-events-none absolute start-2 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, description or linked item" className="field h-8 ps-7" dir="auto" />
          </div>
          <div className="flex flex-wrap gap-1">
            {kinds.map((k) => (
              <button
                key={k.id}
                type="button"
                onClick={() => setKind(k.id)}
                className={cn("h-7 rounded-full px-2.5 text-[13px]", kind === k.id ? "bg-fg text-canvas" : "text-muted hover:bg-hover")}
              >
                {k.label}
              </button>
            ))}
          </div>
          <select value={linked} onChange={(e) => setLinked(e.target.value as typeof linked)} className="field ms-auto h-8 w-auto py-0">
            <option value="all">Linked & unlinked</option>
            <option value="linked">Linked to items</option>
            <option value="unlinked">Not linked</option>
          </select>
        </div>

        {files.isPending && <PageSpinner />}
        {files.data?.length === 0 && (
          <EmptyState
            icon={DocumentIcon}
            title="No files yet"
            action={
              canEdit && (
                <Button variant="primary" icon={ArrowUpTrayIcon} onClick={() => setUploading(true)}>
                  Upload files
                </Button>
              )
            }
          >
            Keep specifications, contracts, designs and screenshots here, and link them to the epics, sprints and tasks they relate to.
          </EmptyState>
        )}
        {files.data && files.data.length > 0 && visible.length === 0 && <p className="py-10 text-center text-sm text-muted">No files match these filters</p>}
        {visible.length > 0 && <FileList files={visible} />}
      </div>
      {uploading && (
        <UploadDialog
          open
          initialFiles={dropped}
          onClose={() => {
            setUploading(false);
            setDropped([]);
          }}
        />
      )}
    </div>
  );
}
